import { loadingControl } from './LoadingControl';

const API_BASE = 'https://5uqm251uv5.execute-api.ap-northeast-3.amazonaws.com/prod/api';

let refreshLock = null;
let refreshTimer = null;

export const getAccessToken = () => {
  return sessionStorage.getItem('accessToken');
};

export const getAccessTokenExpiresAt = () => {
  return parseInt(sessionStorage.getItem('accessTokenExpiresAt'), 10);
};

export const isLoggedIn = () => {
  return !!getAccessToken();
};

export const login = async (loginCode) => {
  loadingControl(true);
  try {
    const res = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login_code: loginCode, mode: 'normal' })
    });
    if (!res.ok) throw new Error('ログイン失敗');
    const data = await res.json();

    const now = Date.now();
    const expiresAt = now + 10 * 60 * 1000;

    sessionStorage.setItem('accessToken', data.access_token);
    sessionStorage.setItem('accessTokenExpiresAt', expiresAt.toString());
    sessionStorage.setItem('refreshToken', data.refresh_token);
    sessionStorage.setItem('userRole', data.role);
    sessionStorage.setItem('companyName', data.company_name);
    sessionStorage.setItem('userName', data.user_name);
    sessionStorage.setItem('loginCode', loginCode);
    sessionStorage.setItem('companyId', data.company_id);
    sessionStorage.setItem('dailyReportUrl', data.daily_report_url || '');

    startRefreshTimer(); // 🔄 自動更新タイマー開始
    window.dispatchEvent(new Event('storage'));
  } finally {
    loadingControl(false);
  }
};

export const logout = async () => {
  stopRefreshTimer();
  sessionStorage.clear();
  window.dispatchEvent(new Event('storage'));
};

export const refresh = async () => {
  if (refreshLock) return refreshLock;

  let resolver;
  refreshLock = new Promise((resolve) => (resolver = resolve));

  const refreshToken = sessionStorage.getItem('refreshToken');
  if (!refreshToken) {
    logout();
    resolver();
    throw new Error('リフレッシュトークンがありません');
  }

  try {
    const response = await fetch(`${API_BASE}/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    const data = await response.json();
    if (!response.ok) {
      logout();
      resolver();
      throw new Error(data?.message || 'トークン更新に失敗しました');
    }

    const now = Date.now();
    const expiresAt = now + 10 * 60 * 1000;

    sessionStorage.setItem('accessToken', data.access_token);
    sessionStorage.setItem('accessTokenExpiresAt', expiresAt.toString());

    return data.access_token;
  } finally {
    resolver();
    refreshLock = null;
  }
};

export const getValidAccessToken = async () => {
  const token = getAccessToken();
  const expireAt = getAccessTokenExpiresAt();
  const now = Date.now();

  if (!token || !expireAt || expireAt - now < 90 * 1000) {
    return await refresh();
  }

  return token;
};

export const fetchWithAuth = async (path, options = {}, retry = true) => {
  loadingControl(true);

  try {
    const token = await getValidAccessToken();

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers
    });

    if (res.status === 401 && retry) {
      await refresh();
      return fetchWithAuth(path, options, false);
    }

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || 'APIエラーが発生しました。');
    }

    return json;
  } catch (e) {
    throw e;
  } finally {
    loadingControl(false);
  }
};

export const startRefreshTimer = () => {
  if (refreshTimer) return;

  refreshTimer = setInterval(async () => {
    const token = getAccessToken();
    const expireAt = getAccessTokenExpiresAt();
    const now = Date.now();

    if (!token || !expireAt || expireAt - now < 90 * 1000) {
      try {
        await refresh();
      } catch (e) {
        console.warn('アクセストークン自動更新失敗:', e);
        logout();
      }
    }
  }, 60 * 1000);
};

export const stopRefreshTimer = () => {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
};

export const getUserRole = () => {
  const role = sessionStorage.getItem('userRole');
  return role !== null ? Number(role) : 0;
};

export const getCompanyName = () => {
  return sessionStorage.getItem('companyName');
};

export const getUserName = () => {
  return sessionStorage.getItem('userName');
};

export function getCompanyId() {
  return sessionStorage.getItem('companyId');
}

export const getLoginCode = () => {
  return sessionStorage.getItem('loginCode');
};

export const getDailyReportUrl = () => {
  return sessionStorage.getItem('dailyReportUrl') || '';
};

window.addEventListener('beforeunload', () => {
  sessionStorage.removeItem('refreshToken');
});
